import type { Campaign } from './data';
import { validDay } from './liveDataAdapter';

/** Resolve legacy links without choosing a campaign on the reader's behalf. */
export function reportSelection(input: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams(input);
  params.set('fpcurrent', input.get('fpcurrent') ?? '');
  const only = (input.get('fpOnly') ?? input.get('currentOnly') ?? '1') !== '0';
  params.set('fpOnly', only ? '1' : '0');
  params.set('fpcomparison', only ? '' : (input.get('fpcomparison') ?? ''));
  for (const key of ['from', 'to', 'cmpFrom', 'cmpTo']) params.set('fp' + key, input.get('fp' + key) ?? '');
  params.set('fpView', input.get('fpView') === 'PCM' || input.get('fpTab') === 'pcm' ? 'PCM' : 'Vendor');
  params.delete('fpPrepare');
  params.delete('fpTab');
  params.delete('currentOnly');
  return params;
}

/** Presentation controls reuse the same validated population and calculations. */
export function reportDataSelection(input: URLSearchParams): URLSearchParams {
  const params = reportSelection(input);
  // Once explicitly chosen, the comparison stays in the shared source population.
  // Current-only hides it; it must not invalidate/refetch that already loaded pair.
  params.set('fpcomparison', input.get('fpcomparison') ?? '');
  for (const key of ['fpView', 'fpOnly', 'fpaxisStart', 'fpaxisDays', 'fpperiod', 'period']) params.delete(key);
  params.sort();
  return params;
}

export function chooseReportOffer(
  input: URLSearchParams,
  rows: Campaign[],
  id: string,
  cutoff: string,
  comparison = false,
) {
  const selected = rows.filter((row) => row.offer_bk === id);
  if (!selected.length) throw new Error('Choose an offer from the loaded catalogue.');
  const { from, to, problem } = reportOfferWindows(selected, cutoff).get(id);
  if (problem) throw new Error(problem);
  const params = new URLSearchParams(input);
  params.set(comparison ? 'fpcomparison' : 'fpcurrent', id);
  params.set(comparison ? 'fpcmpFrom' : 'fpfrom', from);
  params.set(comparison ? 'fpcmpTo' : 'fpto', to);
  params.set('fproom', 'All');
  if (comparison) {
    if (id === params.get('fpcurrent')) throw new Error('Choose a different comparison offer.');
    params.set('fpOnly', '0');
  } else {
    params.set('fpcomparison', '');
    params.set('fpcmpFrom', '');
    params.set('fpcmpTo', '');
    params.set('fpOnly', '1');
    params.delete('fpcurrency');
    params.delete('currency');
  }
  params.delete('fpPrepare');
  params.delete('fpTab');
  return params;
}

/** Requested opening report; resolve dates from today's catalogue rather than freezing them here. */
export function initialReportSelection(input: URLSearchParams, rows: Campaign[], cutoff: string) {
  const current = chooseReportOffer(input, rows, '006RF00000LYwIPYA1', cutoff);
  return chooseReportOffer(current, rows, '006RF00000CixwkYAB', cutoff, true);
}

/** Group once so catalogue row availability does not scan the full catalogue per row. */
export function reportOfferWindows(rows: Campaign[], cutoff: string) {
  const windows = new Map<string, { from: string; to: string; problem?: string }>();
  for (const row of rows) {
    const previous = windows.get(row.offer_bk);
    const from = previous ? (row.start_date < previous.from ? row.start_date : previous.from) : row.start_date;
    const end = row.end_date === null || row.end_date > cutoff ? cutoff : row.end_date;
    const to = previous && previous.to > end ? previous.to : end;
    const invalid = !validDay(row.start_date) || (row.end_date !== null && !validDay(row.end_date));
    windows.set(row.offer_bk, {
      from,
      to,
      problem:
        invalid || previous?.problem
          ? 'Source campaign dates are unavailable. This offer cannot be selected automatically.'
          : undefined,
    });
  }
  for (const window of windows.values()) {
    if (!window.problem && (!validDay(window.to) || window.from > window.to))
      window.problem = 'This offer has no completed reporting dates.';
  }
  return windows;
}

export function searchReportOffers(rows: Campaign[], search: string, excluded = '') {
  const terms = search.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter((row) => {
    const text = [
      row.offer_opportunity_name,
      row.offer_bk,
      row.vendor_name,
      row.vendor_parent_name,
      row.offer_owner_name,
      row.offer_country_name,
      row.offer_geo_area_level_1,
      row.product_name,
    ]
      .join(' ')
      .toLocaleLowerCase();
    return row.offer_bk !== excluded && terms.every((term) => text.includes(term));
  });
}

export const OFFER_FILTER_FIELDS = [
  { key: 'country', label: 'Hotel country' },
  { key: 'vendor', label: 'Hotel / vendor' },
  { key: 'owner', label: 'Owner' },
  { key: 'year', label: 'Campaign year' },
] as const;
export type OfferFilterKey = (typeof OFFER_FILTER_FIELDS)[number]['key'];
export type OfferCatalogueFilters = Partial<Record<OfferFilterKey, string[]>>;

const OFFER_FILTER_COLUMNS = {
  country: 'offer_country_name',
  vendor: 'vendor_name',
  owner: 'offer_owner_name',
} as const;

export function reportOfferFilterValue(row: Campaign, key: OfferFilterKey): string {
  const value =
    key === 'year' ? (validDay(row.start_date) ? row.start_date.slice(0, 4) : null) : row[OFFER_FILTER_COLUMNS[key]];
  return value || 'Unknown';
}

/** OR within a field, AND across fields; never mutate or discard source metadata. */
export function filterReportOffers(rows: Campaign[], filters: OfferCatalogueFilters): Campaign[] {
  return rows.filter((row) =>
    OFFER_FILTER_FIELDS.every(
      ({ key }) => !filters[key]?.length || filters[key].includes(reportOfferFilterValue(row, key)),
    ),
  );
}

export function newestReportOffers(rows: Campaign[]): Campaign[] {
  return [...rows].sort(
    (a, b) =>
      b.start_date.localeCompare(a.start_date) || a.offer_opportunity_name.localeCompare(b.offer_opportunity_name),
  );
}
